import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import styles from "./ui.module.css";
import TextReveal, { revealDuration } from "../ANIMATION/TextReveal";
import { EASE } from "../../UTILS/ANIMATION/motionVariants";

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const Button = ({
  href,
  variant = "primary",
  tone = "dark",
  children,
  className,
  ...rest
}) => {
  const classes = cx(styles.button, styles[variant], styles[`on-${tone}`], className);
  const external = /^https?:|^mailto:|^tel:/.test(href || "");
  if (!href) return <button type="button" className={classes} {...rest}>{children}</button>;
  if (external) {
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={classes} {...rest}>
      {children}
      {variant === "link" && (
        <span aria-hidden="true" className={styles.arrow}>
          →
        </span>
      )}
    </Link>
  );
};

export const Section = ({
  tone = "light",
  id,
  className,
  children,
  labelledBy,
  ...rest
}) => (
  <section
    id={id}
    aria-labelledby={labelledBy}
    className={cx(styles.section, styles[`tone-${tone}`], className)}
    {...rest}
  >
    <div className={styles.container}>{children}</div>
  </section>
);

// Eyebrow fades in, the title arrives word by word, then the lead rises in
// as the last words land, so the section reads in order.
const TITLE_DELAY = 0.12;
const inView = { initial: "hidden", whileInView: "visible", viewport: { once: true, amount: 0.3 } };
const rise = (delay) => ({
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: EASE } },
});

export const SectionIntro = ({
  eyebrow,
  title,
  lead,
  id,
  align = "left",
  as: Heading = "h2",
}) => {
  const reduce = useReducedMotion();
  const leadDelay = Math.max(TITLE_DELAY, revealDuration(title, TITLE_DELAY) - 0.35);
  return (
    <header className={cx(styles.intro, align === "center" && styles.center)}>
      {eyebrow &&
        (reduce ? (
          <p className={styles.eyebrow}>{eyebrow}</p>
        ) : (
          <motion.p className={styles.eyebrow} variants={rise(0)} {...inView}>
            {eyebrow}
          </motion.p>
        ))}
      <TextReveal as={Heading} id={id} className={styles.title} delay={TITLE_DELAY}>
        {title}
      </TextReveal>
      {lead &&
        (reduce ? (
          <p className={styles.lead}>{lead}</p>
        ) : (
          <motion.p className={styles.lead} variants={rise(leadDelay)} {...inView}>
            {lead}
          </motion.p>
        ))}
    </header>
  );
};
